<template>
  <p-dialog
    :visible.sync="visible"
    header="Add New Credit Card"
    :closable="true"
    :modal="true"
    @hide="onHide"
  >
    <p-inlinemessage v-if="errorMsg" severity="error" class="p-mb-3 style-msg">
      {{ errorMsg }}
    </p-inlinemessage>

    <div class="p-fluid">
      <div class="p-field p-mb-3">
        <label for="cardholderName" class="p-mb-1" style="display: block;">Cardholder Name</label>
        <p-inputText
          id="cardholderName"
          v-model="cardholderName"
          placeholder="Name on card"
        />
      </div>

      <div class="p-field p-mb-3">
        <label class="p-mb-1" style="display: block;">Card Details</label>
        <div id="stripe-card-element" class="stripe-card-container"></div>
      </div>

      <div class="p-field-checkbox p-mt-3" style="display: flex; align-items: center;">
        <p-checkbox id="setAsDefault" v-model="setAsDefault" :binary="true" />
        <label for="setAsDefault" class="p-ml-2" style="cursor: pointer;">Set as default card</label>
      </div>
    </div>

    <template #footer>
      <p-button
        label="Add Card"
        icon="pi pi-check"
        :loading="loading"
        :disabled="loading"
        @click="handleAddCard"
      />
      <p-button
        label="Cancel"
        icon="pi pi-times"
        class="p-button-secondary"
        :disabled="loading"
        @click="onHide"
      />
    </template>
  </p-dialog>
</template>

<script>
import { loadStripe } from '@stripe/stripe-js'
import Config from '../../../config'
import { addSavedCard, createSetupIntent, setDefaultSavedCard } from '../../../services/user'

export default {
  name: 'AddCardModal',
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      stripe: null,
      elements: null,
      cardElement: null,
      cardholderName: '',
      setAsDefault: false,
      loading: false,
      errorMsg: '',
    }
  },
  watch: {
    async visible(val) {
      if (val) {
        this.errorMsg = ''
        this.cardholderName = ''
        this.setAsDefault = false
        this.$nextTick(async () => {
          await this.initStripe()
        })
      } else {
        this.destroyStripe()
      }
    },
  },
  methods: {
    async initStripe() {
      try {
        const key = Config.STRIPE_PUBLISHABLE_KEY || 'pk_test_TYooMQauvdEDq54NiTphI7jx'
        this.stripe = await loadStripe(key)
        if (!this.stripe) {
          this.errorMsg = 'Failed to load Stripe SDK'
          return
        }
        this.elements = this.stripe.elements()
        this.cardElement = this.elements.create('card', {
          style: {
            base: {
              fontSize: '15px',
              color: '#32325d',
              '::placeholder': {
                color: '#aab7c4',
              },
            },
            invalid: {
              color: '#fa755a',
              iconColor: '#fa755a',
            },
          },
        })
        const container = document.getElementById('stripe-card-element')
        if (container) {
          container.innerHTML = ''
          this.cardElement.mount('#stripe-card-element')
        }
      } catch (err) {
        console.error('Error initializing Stripe:', err)
        this.errorMsg = 'Error initializing payment form'
      }
    },
    destroyStripe() {
      if (this.cardElement) {
        try {
          this.cardElement.unmount()
          this.cardElement.destroy()
        } catch (e) {
          // ignore
        }
        this.cardElement = null
      }
    },
    async handleAddCard() {
      this.errorMsg = ''
      if (!this.stripe || !this.cardElement) {
        this.errorMsg = 'Stripe is not ready'
        return
      }

      this.loading = true
      try {
        const { paymentMethod, error } = await this.stripe.createPaymentMethod({
          type: 'card',
          card: this.cardElement,
          billing_details: {
            name: this.cardholderName || undefined,
          },
        })

        if (error) {
          this.errorMsg = error.message || 'Invalid card details'
          this.loading = false
          return
        }

        const res = await addSavedCard(paymentMethod.id, this.setAsDefault)
        if (res && res.success) {
          this.$emit('cardAdded')
          this.onHide()
        } else {
          this.errorMsg = res?.message || 'Failed to save card'
        }
      } catch (err) {
        console.error('Error adding card:', err)
        this.errorMsg = 'An error occurred while saving card'
      } finally {
        this.loading = false
      }
    },

    onHide() {
      this.$emit('update:visible', false)
      this.$emit('toggleModal', false)
    },
  },
}
</script>

<style scoped>
.stripe-card-container {
  padding: 10px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  background-color: #ffffff;
  min-height: 40px;
}
.style-msg {
  width: 100%;
}
</style>
